/**
 * Help & support (service-core V170, workspace docs/support.md): the types and
 * calls behind Platform › Support (the inbox) and the requesters' Help & support
 * (shops, couriers and partner desks). One contract for every app; the side a
 * request is opened as is checked on the server.
 *
 * Every query key starts with ['support'] (the requester's own) or
 * ['admin-support'] (the platform's), so one `support` frame on the live stream
 * refreshes all of it (lib/liveEvents).
 */
import { api } from './api'
import type { Persona } from './personas'

export type SupportSide = 'client' | 'courier' | 'shop' | 'partner'
export type SupportStatus = 'open' | 'answered' | 'closed'
export type SupportAuthorSide = 'requester' | 'platform' | 'system'

export interface SupportAttachment {
  id: string
  /** Signed and absolute; lives 1–2 h. Used as it is in <img src>. */
  url: string
  contentType: string
  sizeBytes: number
}

export interface SupportMessage {
  id: string
  authorSide: SupportAuthorSide
  /** Requester view: "TOU support" for the platform. Platform view: the admin who wrote it. */
  authorName?: string | null
  body: string
  /** Platform view only: an internal note, never shown to the requester. */
  internal?: boolean | null
  createdAt: string
  attachments?: SupportAttachment[] | null
}

/** One row of the requester's own list (GET /api/support/requests). */
export interface SupportRequestSummary {
  id: string
  /** 1001 on the wire; shown as "S-1001". */
  number: number | string
  side: SupportSide
  category: string
  categoryLabel?: string | null
  subject?: string | null
  status: SupportStatus
  orderId?: string | null
  orderNr?: string | null
  courierOrderId?: string | null
  lastMessageAt: string
  lastMessageSide: 'requester' | 'platform'
  unread: boolean
  createdAt: string
  closedAt?: string | null
}

export interface SupportRequestDetail extends SupportRequestSummary {
  closedBySide?: 'requester' | 'platform' | 'system' | null
  messages: SupportMessage[]
  email?: string | null
  /** Only in the answer to a create: the first message, for its photos. */
  messageId?: string | null
}

export interface SupportOptions {
  categories: { code: string; label: string }[]
  canLinkOrders: boolean
  email: string
}

export interface SupportPage<T> {
  items: T[]
  total: number
  page: number
  size: number
}

/** A row of Platform › Support. */
export interface AdminSupportRow {
  id: string
  number: number | string
  side: SupportSide
  category: string
  categoryLabel?: string | null
  subject?: string | null
  status: SupportStatus
  requesterId: string
  requesterName?: string | null
  requesterEmail?: string | null
  companyId?: string | null
  companyName?: string | null
  orderId?: string | null
  orderNr?: string | null
  courierOrderId?: string | null
  /** At most 140 characters, never an internal note. */
  lastMessagePreview?: string | null
  lastMessageAt: string
  lastMessageSide: 'requester' | 'platform'
  platformUnread: boolean
  sourceApp: string
  createdAt: string
}

export interface AdminSupportCounts {
  open: number
  answered: number
  closed: number
}

export interface AdminSupportList extends SupportPage<AdminSupportRow> {
  /** Every status under the other filters, whatever the tab. */
  counts: AdminSupportCounts
}

export interface AdminSupportDetail {
  id: string
  number: number | string
  side: SupportSide
  category: string
  categoryLabel?: string | null
  subject?: string | null
  status: SupportStatus
  requester: { id: string; name?: string | null; email?: string | null; phone?: string | null; language?: string | null }
  company?: { id: string; name?: string | null; kind: 'company' | 'partner' } | null
  orderId?: string | null
  orderNr?: string | null
  courierOrderId?: string | null
  sourceApp: string
  appVersion?: string | null
  platform?: string | null
  lastMessageAt: string
  lastMessageSide: 'requester' | 'platform'
  platformUnread: boolean
  createdAt: string
  closedAt?: string | null
  closedBySide?: 'requester' | 'platform' | 'system' | null
  closedByName?: string | null
  messages: SupportMessage[]
}

export interface SupportSummary {
  needsReply: number
  answered: number
}

export interface SupportSettings {
  /** Comma list; blank = every platform admin. */
  notifyEmails: string
  /** 0–90; 0 = never. */
  autoCloseDays: number
  updatedAt?: string | null
  updatedBy?: string | null
  updatedByName?: string | null
}

export interface CreateSupportRequest {
  side: SupportSide
  category: string
  subject?: string
  body: string
  orderId?: string
  courierOrderId?: string
  sourceApp: 'backoffice'
  appVersion?: string
  platform?: string
}

// ── Words ─────────────────────────────────────────────────────────────────

export const SIDE_LABEL: Record<SupportSide, string> = {
  client: 'Client',
  courier: 'Courier',
  shop: 'Shop',
  partner: 'Partner',
}

export const SIDES: SupportSide[] = ['client', 'courier', 'shop', 'partner']

/** How the platform reads a status (the inbox's tabs). */
export const ADMIN_STATUS_LABEL: Record<SupportStatus, string> = {
  open: 'Needs reply',
  answered: 'Answered',
  closed: 'Closed',
}

/** How a requester reads a status, as TOU and the field app say it. */
export const REQUESTER_STATUS_LABEL: Record<SupportStatus, string> = {
  open: 'Waiting for TOU',
  answered: 'Answered',
  closed: 'Closed',
}

export const SOURCE_APP_LABEL: Record<string, string> = {
  tou: 'TOU',
  field: 'TOU Field',
  backoffice: 'Backoffice',
}

/**
 * The category codes per side (docs/support.md § Categories) with service-core's
 * English labels, for the inbox's category filter. Rows and threads carry the
 * server's own `categoryLabel`.
 */
export const SUPPORT_CATEGORIES: Record<SupportSide, string[]> = {
  client: ['order_late', 'order_wrong_or_missing', 'order_damaged', 'payment_refund', 'change_or_cancel', 'account', 'app_problem', 'other'],
  courier: ['job_problem', 'address_or_client', 'pickup_problem', 'earnings_payout', 'app_problem', 'other'],
  shop: ['order_problem', 'courier_problem', 'payouts_wallet', 'catalog_offerings', 'printer_devices', 'app_problem', 'other'],
  partner: ['job_problem', 'zones_rates', 'invoicing', 'other'],
}

export const CATEGORY_LABEL: Record<string, string> = {
  order_late: 'My order is late',
  order_wrong_or_missing: 'Something is wrong or missing',
  order_damaged: 'Something arrived damaged',
  payment_refund: 'Payment or refund',
  change_or_cancel: 'Change or cancel an order',
  account: 'My account',
  app_problem: 'Problem with the app',
  job_problem: 'Problem with a delivery',
  address_or_client: 'Address or client',
  pickup_problem: 'Problem at pickup',
  earnings_payout: 'Earnings or payout',
  order_problem: 'Problem with an order',
  courier_problem: 'Problem with a courier',
  payouts_wallet: 'Payouts or wallet',
  catalog_offerings: 'Catalog or offerings',
  printer_devices: 'Printer or devices',
  zones_rates: 'Zones or rates',
  invoicing: 'Invoicing',
  other: 'Something else',
}

/** The filter's categories: the side's own, or every code once when no side is picked. */
export function categoriesForSide(side: SupportSide | '' | null | undefined): string[] {
  if (side && SUPPORT_CATEGORIES[side]) return SUPPORT_CATEGORIES[side]
  return [...new Set(SIDES.flatMap((s) => SUPPORT_CATEGORIES[s]))]
}

export function categoryLabel(code: string, serverLabel?: string | null): string {
  return serverLabel?.trim() || CATEGORY_LABEL[code] || code.replace(/_/g, ' ')
}

/** "S-1001" from the wire's 1001 (or an "S-1001" that is already one). */
export function supportNumber(number: number | string | null | undefined): string {
  if (number == null || number === '') return ''
  const text = String(number).trim()
  return /^S-/i.test(text) ? text.toUpperCase() : `S-${text}`
}

/** The row's title: its subject, else its category. */
export function requestTitle(row: { subject?: string | null; category: string; categoryLabel?: string | null }): string {
  return row.subject?.trim() || categoryLabel(row.category, row.categoryLabel)
}

/** The side a backoffice persona asks as. The platform (Admin) answers, it doesn't ask. */
export function supportSideForPersona(persona: Persona | 'None' | null | undefined): SupportSide | null {
  switch (persona) {
    case 'Merchant':
    case 'Manager':
    case 'Worker':
      return 'shop'
    case 'Courier':
      return 'courier'
    case 'Partner':
      return 'partner'
    default:
      return null
  }
}

// ── Polling and errors ────────────────────────────────────────────────────

/** While the live stream is down: the badges and an open thread, this often. */
export const SUPPORT_POLL_MS = 60_000

export function supportRefetchInterval(liveConnected: boolean): number | false {
  return liveConnected ? false : SUPPORT_POLL_MS
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status
}

export function isNotFound(err: unknown): boolean {
  return statusOf(err) === 404
}

/**
 * React Query `retry` for the support reads the shell makes on its own (options,
 * badges): a 4xx is an answer — 404 is a service-core without V170 — so only a
 * 5xx is asked again, twice. Without a connection there is nothing to ask;
 * React Query reads again once the browser is back online.
 */
export function supportQueryRetry(failures: number, err: unknown): boolean {
  const status = statusOf(err)
  return typeof status === 'number' && status >= 500 && failures < 2
}

/** A 409 `{ code: "already_open", id, number }`: the thread to open instead, or null. */
export function alreadyOpenRequest(err: unknown): { id: string; number: string } | null {
  if (statusOf(err) !== 409) return null
  const data = (err as { response?: { data?: unknown } }).response?.data as Record<string, unknown> | undefined
  if (!data || data.code !== 'already_open' || typeof data.id !== 'string' || !data.id) return null
  return { id: data.id, number: supportNumber(data.number as string | number | undefined) }
}

// ── Photos ────────────────────────────────────────────────────────────────

export const MAX_PHOTOS = 3
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
export const PHOTO_ACCEPT = PHOTO_TYPES.join(',')

/**
 * Adds picked files to the ones already chosen: images only, 5 MB each, three
 * in all (the server's limits). Returns what is kept and why the rest is not.
 */
export function addPhotos(current: File[], picked: File[]): { files: File[]; error: string | null } {
  const files = [...current]
  const problems: string[] = []
  for (const file of picked) {
    if (!PHOTO_TYPES.includes(file.type)) {
      problems.push(`${file.name} is not a JPEG, PNG, WebP or GIF image.`)
    } else if (file.size > MAX_PHOTO_BYTES) {
      problems.push(`${file.name} is over 5 MB.`)
    } else if (files.length >= MAX_PHOTOS) {
      problems.push(`Up to ${MAX_PHOTOS} photos per message.`)
      break
    } else {
      files.push(file)
    }
  }
  return { files, error: problems.length ? problems.join(' ') : null }
}

// ── Calls ─────────────────────────────────────────────────────────────────

/** The backoffice is in English; labels would otherwise follow the browser's language. */
const ENGLISH = { 'Accept-Language': 'en' }

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } }

function photoForm(file: File): FormData {
  const form = new FormData()
  form.append('file', file)
  return form
}

/**
 * Uploads each photo to a message that was just written, one call per photo.
 * The message is sent either way; the photos that didn't make it come back
 * with the reason.
 */
export async function uploadPhotos(
  upload: (file: File) => Promise<unknown>,
  files: File[],
): Promise<{ file: File; error: unknown }[]> {
  const failed: { file: File; error: unknown }[] = []
  for (const file of files) {
    try {
      await upload(file)
    } catch (error) {
      failed.push({ file, error })
    }
  }
  return failed
}

/** The requester's side: /api/support. */
export const supportApi = {
  options: async (side: SupportSide) =>
    (await api.get<SupportOptions>('/support/options', { params: { side }, headers: ENGLISH })).data,
  list: async (page: number, size: number) =>
    (await api.get<SupportPage<SupportRequestSummary>>('/support/requests', {
      params: { page, size },
      headers: ENGLISH,
    })).data,
  unreadCount: async () => (await api.get<{ count: number }>('/support/unread-count')).data,
  create: async (body: CreateSupportRequest) =>
    (await api.post<SupportRequestDetail>('/support/requests', body, { headers: ENGLISH })).data,
  get: async (id: string) =>
    (await api.get<SupportRequestDetail>(`/support/requests/${id}`, { headers: ENGLISH })).data,
  reply: async (id: string, body: string) =>
    (await api.post<SupportMessage>(`/support/requests/${id}/messages`, { body })).data,
  attach: async (id: string, messageId: string, file: File) =>
    (await api.post<SupportAttachment>(
      `/support/requests/${id}/messages/${messageId}/attachments`,
      photoForm(file),
      multipart,
    )).data,
  read: async (id: string) => {
    await api.post(`/support/requests/${id}/read`)
  },
  close: async (id: string) =>
    (await api.post<SupportRequestDetail>(`/support/requests/${id}/close`, undefined, { headers: ENGLISH })).data,
}

export interface AdminSupportFilters {
  status: SupportStatus | 'all'
  side?: SupportSide | ''
  category?: string
  q?: string
  orderId?: string
  page: number
  size: number
}

/** The platform's side: /api/admin/support. */
export const adminSupportApi = {
  list: async ({ status, side, category, q, orderId, page, size }: AdminSupportFilters) =>
    (await api.get<AdminSupportList>('/admin/support/requests', {
      params: {
        status,
        side: side || undefined,
        category: category || undefined,
        q: q?.trim() || undefined,
        orderId: orderId || undefined,
        page,
        size,
      },
    })).data,
  get: async (id: string) => (await api.get<AdminSupportDetail>(`/admin/support/requests/${id}`)).data,
  reply: async (id: string, body: string, internal: boolean) =>
    (await api.post<SupportMessage>(`/admin/support/requests/${id}/messages`, { body, internal })).data,
  attach: async (id: string, messageId: string, file: File) =>
    (await api.post<SupportAttachment>(
      `/admin/support/requests/${id}/messages/${messageId}/attachments`,
      photoForm(file),
      multipart,
    )).data,
  setStatus: async (id: string, status: SupportStatus) =>
    (await api.patch<AdminSupportDetail>(`/admin/support/requests/${id}`, { status })).data,
  read: async (id: string) => {
    await api.post(`/admin/support/requests/${id}/read`)
  },
  summary: async () => (await api.get<SupportSummary>('/admin/support/summary')).data,
  settings: async () => (await api.get<SupportSettings>('/admin/support/settings')).data,
  saveSettings: async (body: Partial<Pick<SupportSettings, 'notifyEmails' | 'autoCloseDays'>>) =>
    (await api.put<SupportSettings>('/admin/support/settings', body)).data,
}

/** Query keys. Everything sits under one of the two prefixes the live stream refreshes. */
export const supportKeys = {
  all: ['support'] as const,
  options: (side: SupportSide) => ['support', 'options', side] as const,
  unread: ['support', 'unread-count'] as const,
  list: (page: number) => ['support', 'requests', page] as const,
  lists: ['support', 'requests'] as const,
  detail: (id: string) => ['support', 'request', id] as const,
  orderChoices: (side: SupportSide) => ['support', 'order-choices', side] as const,
}

export const adminSupportKeys = {
  all: ['admin-support'] as const,
  summary: ['admin-support', 'summary'] as const,
  lists: ['admin-support', 'list'] as const,
  list: (filters: AdminSupportFilters) => ['admin-support', 'list', filters] as const,
  detail: (id: string) => ['admin-support', 'request', id] as const,
  settings: ['admin-support', 'settings'] as const,
}

/** What the backoffice sends as `appVersion` and `platform` with a new request. */
export function backofficeClientInfo(): { appVersion?: string; platform: string } {
  const release = String(import.meta.env.VITE_SENTRY_RELEASE ?? '').trim()
  return { ...(release ? { appVersion: release } : {}), platform: 'web' }
}
